"use client";

import React, { useState, useMemo } from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Box,
  Sliders,
  RotateCcw,
} from "lucide-react";

type ModeId = "ai_bug" | "user_hack" | "fix_between" | "fix_on_top";

interface PresetMode {
  id: ModeId;
  label: string;
  badge: string;
  description: string;
}

const PRESETS: PresetMode[] = [
  {
    id: "ai_bug",
    label: "1. Original-KI-Liste (Fehlerhaft)",
    badge: "3,2 cm Überstand",
    description:
      "Die KI legt Deckel und Boden (15,0 cm) zwischen die 22,0 cm hohen Seitenwände, rechnet aber für die Frontplatte 25,2 cm Höhe aus.",
  },
  {
    id: "user_hack",
    label: "2. Realer Werkstatt-Rettungsbau (90°-Drehung)",
    badge: "Standkufen-Lösung",
    description:
      "Ohne Nachsägen gedreht: Die 25,2 × 18,2 cm Platten sitzen seitlich als Wangen; der 18,2 cm tiefe Kern schließt vorne, hinten und oben bündig ab, unten entstehen 3,2 cm hohe Füße.",
  },
  {
    id: "fix_between",
    label: "3. Korrekt A: Deckel zwischen Seiten",
    badge: "Bündig · 4,46 l",
    description:
      "Damit die Front (25,2 cm) bündig passt und innen 22,0 cm Höhe bleiben, müssen die durchlaufenden Seitenwände ebenfalls 25,2 cm hoch sein.",
  },
  {
    id: "fix_on_top",
    label: "4. Korrekt B: Deckel auf Seiten",
    badge: "Bündig · 4,46 l",
    description:
      "Bleiben die Seitenwände bei 22,0 cm Höhe, müssen Deckel und Boden oben und unten aufliegen – und dafür die volle Breite von 18,2 cm erhalten.",
  },
];

export default function Playground({ locale = "de" }: { locale?: "de" | "en" }) {
  const isEn = locale === "en";
  const [mode, setMode] = useState<ModeId>("ai_bug");
  const [thicknessMm, setThicknessMm] = useState<number>(16);
  const [stackOrder, setStackOrder] = useState<"between" | "on_top">("between");

  const tCm = thicknessMm / 10; // e.g., 1.6 cm
  const targetInnerW = 15.0;
  const targetInnerH = 22.0;
  const targetInnerD = 13.5;
  const targetVolL = (targetInnerW * targetInnerH * targetInnerD) / 1000;

  const calc = useMemo(() => {
    const twoT = 2 * tCm;

    if (mode === "ai_bug") {
      // AI original logic: Front = (22 + 2t) x (15 + 2t), Sides = 22 x 13.5, Top/Bottom = 15 x 13.5
      const frontH = +(targetInnerH + twoT).toFixed(2);
      const frontW = +(targetInnerW + twoT).toFixed(2);
      const sideH = targetInnerH;
      const sideD = targetInnerD;
      const topW = targetInnerW;
      const topD = targetInnerD;

      if (stackOrder === "between") {
        const frameW = +(topW + twoT).toFixed(2);
        const frameH = sideH;
        const innerW = topW;
        const innerH = +(sideH - twoT).toFixed(2);
        const innerD = sideD;
        const volL = +((innerW * innerH * innerD) / 1000).toFixed(2);
        const diffW = +(frontW - frameW).toFixed(2);
        const diffH = +(frontH - frameH).toFixed(2);

        return {
          frontDim: `${frontH.toFixed(1)} × ${frontW.toFixed(1)} cm`,
          sideDim: `${sideH.toFixed(1)} × ${sideD.toFixed(1)} cm`,
          topDim: `${topW.toFixed(1)} × ${topD.toFixed(1)} cm`,
          frontFormula: `(${targetInnerH.toFixed(1)} + 2×${tCm.toFixed(1)}) × (${targetInnerW.toFixed(1)} + 2×${tCm.toFixed(1)})`,
          sideFormula: `${targetInnerH.toFixed(1)} (ohne +2×${tCm.toFixed(1)}!) × ${targetInnerD.toFixed(1)}`,
          topFormula: `${targetInnerW.toFixed(1)} × ${targetInnerD.toFixed(1)}`,
          frameOuterW: frameW,
          frameOuterH: frameH,
          frontOuterW: frontW,
          frontOuterH: frontH,
          diffW,
          diffH,
          innerDims: `${innerW.toFixed(1)} × ${innerH.toFixed(1)} × ${innerD.toFixed(1)} cm`,
          volL,
          isFlush: Math.abs(diffW) < 0.01 && Math.abs(diffH) < 0.01,
          invariantH: `${frameH.toFixed(1)} cm (Rahmen) ≠ ${frontH.toFixed(1)} cm (Front) → +${diffH.toFixed(1)} cm Überstand ("Füße")`,
          invariantW: `${topW.toFixed(1)} + 2×${tCm.toFixed(1)} = ${frameW.toFixed(1)} cm == ${frontW.toFixed(1)} cm (Front)`,
        };
      } else {
        // stackOrder === "on_top" with AI bug boards
        const frameW = topW;
        const frameH = +(sideH + twoT).toFixed(2);
        const innerW = +(topW - twoT).toFixed(2);
        const innerH = sideH;
        const innerD = sideD;
        const volL = +((innerW * innerH * innerD) / 1000).toFixed(2);
        const diffW = +(frontW - frameW).toFixed(2);
        const diffH = +(frontH - frameH).toFixed(2);

        return {
          frontDim: `${frontH.toFixed(1)} × ${frontW.toFixed(1)} cm`,
          sideDim: `${sideH.toFixed(1)} × ${sideD.toFixed(1)} cm`,
          topDim: `${topW.toFixed(1)} × ${topD.toFixed(1)} cm`,
          frontFormula: `(${targetInnerH.toFixed(1)} + 2×${tCm.toFixed(1)}) × (${targetInnerW.toFixed(1)} + 2×${tCm.toFixed(1)})`,
          sideFormula: `${targetInnerH.toFixed(1)} × ${targetInnerD.toFixed(1)}`,
          topFormula: `${targetInnerW.toFixed(1)} (ohne +2×${tCm.toFixed(1)}!) × ${targetInnerD.toFixed(1)}`,
          frameOuterW: frameW,
          frameOuterH: frameH,
          frontOuterW: frontW,
          frontOuterH: frontH,
          diffW,
          diffH,
          innerDims: `${innerW.toFixed(1)} × ${innerH.toFixed(1)} × ${innerD.toFixed(1)} cm`,
          volL,
          isFlush: Math.abs(diffW) < 0.01 && Math.abs(diffH) < 0.01,
          invariantH: `${sideH.toFixed(1)} + 2×${tCm.toFixed(1)} = ${frameH.toFixed(1)} cm == ${frontH.toFixed(1)} cm (Front)`,
          invariantW: `${frameW.toFixed(1)} cm (Deckel) ≠ ${frontW.toFixed(1)} cm (Front) → +${diffW.toFixed(1)} cm seitlicher Überstand`,
        };
      }
    }

    if (mode === "user_hack") {
      // User rotated the boards:
      // Outer side panels = 25.2 (H) x 18.2 (D)
      // Inner front/back = 22.0 (H) x 13.5 (W)
      // Inner top/bottom = 15.0 (D) x 13.5 (W) clamped between inner front/back
      const outerSideH = +(targetInnerH + twoT).toFixed(2); // 25.2
      const outerSideD = +(targetInnerW + twoT).toFixed(2); // 18.2
      const coreFrontH = targetInnerH; // 22.0
      const coreFrontW = targetInnerD; // 13.5
      const coreTopD = targetInnerW; // 15.0
      const coreTopW = targetInnerD; // 13.5

      const coreOuterD = +(coreTopD + twoT).toFixed(2); // 15.0 + 3.2 = 18.2
      const totalOuterW = +(coreFrontW + twoT).toFixed(2); // 13.5 + 3.2 = 16.7
      const innerW = coreFrontW; // 13.5
      const innerH = +(coreFrontH - twoT).toFixed(2); // 18.8
      const innerD = coreTopD; // 15.0
      const volL = +((innerW * innerH * innerD) / 1000).toFixed(2);
      const legOverhang = +(outerSideH - coreFrontH).toFixed(2); // 3.2 cm legs at bottom

      return {
        frontDim: `${outerSideH.toFixed(1)} × ${outerSideD.toFixed(1)} cm (als Seitenwangen!)`,
        sideDim: `${coreFrontH.toFixed(1)} × ${coreFrontW.toFixed(1)} cm (als Front/Heck innen)`,
        topDim: `${coreTopD.toFixed(1)} × ${coreTopW.toFixed(1)} cm (Deckel/Boden innen)`,
        frontFormula: `Außenhöhe ${outerSideH.toFixed(1)} × Außentiefe ${outerSideD.toFixed(1)}`,
        sideFormula: `Korpushöhe ${coreFrontH.toFixed(1)} × Innenbreite ${coreFrontW.toFixed(1)}`,
        topFormula: `Innentiefe ${coreTopD.toFixed(1)} × Innenbreite ${coreTopW.toFixed(1)}`,
        frameOuterW: coreOuterD,
        frameOuterH: coreFrontH,
        frontOuterW: outerSideD,
        frontOuterH: outerSideH,
        diffW: 0,
        diffH: legOverhang,
        innerDims: `${innerW.toFixed(1)} (B) × ${innerH.toFixed(1)} (H) × ${innerD.toFixed(1)} (T) cm`,
        volL,
        isFlush: false,
        invariantH: `Oben bündig · Unten ${outerSideH.toFixed(1)} − ${coreFrontH.toFixed(1)} = ${legOverhang.toFixed(1)} cm Standkufen (Gesamthöhe ${outerSideH.toFixed(1)} cm, Breite ${totalOuterW.toFixed(1)} cm)`,
        invariantW: `Tiefe Kern (${coreTopD.toFixed(1)} + 2×${tCm.toFixed(1)} = ${coreOuterD.toFixed(1)} cm) == Tiefe Seitenwange (${outerSideD.toFixed(1)} cm) [100 % bündig]`,
      };
    }

    if (mode === "fix_between") {
      const frontH = +(targetInnerH + twoT).toFixed(2);
      const frontW = +(targetInnerW + twoT).toFixed(2);
      const sideH = +(targetInnerH + twoT).toFixed(2);
      const sideD = targetInnerD;
      const topW = targetInnerW;
      const topD = targetInnerD;

      const frameW = +(topW + twoT).toFixed(2);
      const frameH = sideH;
      const volL = +((targetInnerW * targetInnerH * targetInnerD) / 1000).toFixed(2);

      return {
        frontDim: `${frontH.toFixed(1)} × ${frontW.toFixed(1)} cm`,
        sideDim: `${sideH.toFixed(1)} × ${sideD.toFixed(1)} cm`,
        topDim: `${topW.toFixed(1)} × ${topD.toFixed(1)} cm`,
        frontFormula: `(${targetInnerH.toFixed(1)} + 2×${tCm.toFixed(1)}) × (${targetInnerW.toFixed(1)} + 2×${tCm.toFixed(1)})`,
        sideFormula: `(${targetInnerH.toFixed(1)} + 2×${tCm.toFixed(1)} = ${sideH.toFixed(1)}) × ${sideD.toFixed(1)}`,
        topFormula: `${topW.toFixed(1)} (exakt Innenbreite) × ${topD.toFixed(1)}`,
        frameOuterW: frameW,
        frameOuterH: frameH,
        frontOuterW: frontW,
        frontOuterH: frontH,
        diffW: 0,
        diffH: 0,
        innerDims: `${targetInnerW.toFixed(1)} × ${targetInnerH.toFixed(1)} × ${targetInnerD.toFixed(1)} cm`,
        volL,
        isFlush: true,
        invariantH: `Seitenwand (${sideH.toFixed(1)} cm) == Front-Höhe (${frontH.toFixed(1)} cm) → 0,0 cm Abweichung`,
        invariantW: `Deckel (${topW.toFixed(1)}) + 2×Seite (${twoT.toFixed(1)}) = ${frameW.toFixed(1)} cm == Front-Breite (${frontW.toFixed(1)} cm)`,
      };
    }

    // mode === "fix_on_top"
    const frontH = +(targetInnerH + twoT).toFixed(2);
    const frontW = +(targetInnerW + twoT).toFixed(2);
    const sideH = targetInnerH;
    const sideD = targetInnerD;
    const topW = +(targetInnerW + twoT).toFixed(2);
    const topD = targetInnerD;

    const frameW = topW;
    const frameH = +(sideH + twoT).toFixed(2);
    const volL = +((targetInnerW * targetInnerH * targetInnerD) / 1000).toFixed(2);

    return {
      frontDim: `${frontH.toFixed(1)} × ${frontW.toFixed(1)} cm`,
      sideDim: `${sideH.toFixed(1)} × ${sideD.toFixed(1)} cm`,
      topDim: `${topW.toFixed(1)} × ${topD.toFixed(1)} cm`,
      frontFormula: `(${targetInnerH.toFixed(1)} + 2×${tCm.toFixed(1)}) × (${targetInnerW.toFixed(1)} + 2×${tCm.toFixed(1)})`,
      sideFormula: `${sideH.toFixed(1)} (exakt Innenhöhe) × ${sideD.toFixed(1)}`,
      topFormula: `(${targetInnerW.toFixed(1)} + 2×${tCm.toFixed(1)} = ${topW.toFixed(1)}) × ${topD.toFixed(1)}`,
      frameOuterW: frameW,
      frameOuterH: frameH,
      frontOuterW: frontW,
      frontOuterH: frontH,
      diffW: 0,
      diffH: 0,
      innerDims: `${targetInnerW.toFixed(1)} × ${targetInnerH.toFixed(1)} × ${targetInnerD.toFixed(1)} cm`,
      volL,
      isFlush: true,
      invariantH: `Seite (${sideH.toFixed(1)}) + 2×Deckel/Boden (${twoT.toFixed(1)}) = ${frameH.toFixed(1)} cm == Front-Höhe (${frontH.toFixed(1)} cm)`,
      invariantW: `Deckel-Breite (${topW.toFixed(1)} cm) == Front-Breite (${frontW.toFixed(1)} cm) → 0,0 cm Abweichung`,
    };
  }, [mode, tCm, stackOrder, targetInnerW, targetInnerH, targetInnerD]);

  const handleReset = () => {
    setMode("ai_bug");
    setThicknessMm(16);
    setStackOrder("between");
  };

  return (
    <div className="flex flex-col gap-5 text-sm">
      {/* Preset Selector */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold tracking-wider text-neutral-500 uppercase dark:text-neutral-400">
            {isEn ? "Select Construction Scenario" : "Bauszenario wählen"}
          </span>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-200/60 dark:text-neutral-400 dark:hover:bg-neutral-800"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{isEn ? "Reset" : "Zurücksetzen"}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PRESETS.map((p) => {
            const active = mode === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setMode(p.id)}
                className={`flex min-h-[56px] flex-col items-start justify-between rounded-xl border p-3 text-left transition-all ${
                  active
                    ? "border-purple-500 bg-purple-500/10 text-neutral-900 dark:border-purple-400 dark:bg-purple-500/15 dark:text-white"
                    : "border-neutral-200/80 bg-white/60 text-neutral-700 hover:border-purple-500/40 dark:border-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-300"
                }`}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-semibold">{p.label}</span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      p.id === "ai_bug"
                        ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                        : p.id === "user_hack"
                          ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                          : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    }`}
                  >
                    {p.badge}
                  </span>
                </div>
                <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                  {p.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Controls: Material Thickness & Stack Order (when in AI bug mode) */}
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3.5 sm:grid-cols-2 dark:border-neutral-800 dark:bg-neutral-900/40">
        <div className="flex flex-col justify-between gap-2">
          <label
            htmlFor="mdf-thickness-select"
            className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700 dark:text-neutral-300"
          >
            <Sliders className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" aria-hidden="true" />
            <span>
              {isEn ? "Board Thickness (Baumarkt MDF)" : "Materialstärke im Baumarkt (MDF)"}
            </span>
          </label>
          <div id="mdf-thickness-select" className="flex gap-2">
            {[12, 16, 19].map((mm) => (
              <button
                key={mm}
                type="button"
                onClick={() => setThicknessMm(mm)}
                className={`min-h-[44px] flex-1 rounded-lg border px-3 py-1.5 font-mono text-xs font-semibold transition-all ${
                  thicknessMm === mm
                    ? "border-purple-500 bg-purple-600 text-white dark:bg-purple-500"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-purple-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                }`}
              >
                {mm} mm ({(mm / 10).toFixed(1)} cm)
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700 dark:text-neutral-300">
            <Box className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" aria-hidden="true" />
            <span>
              {mode === "ai_bug"
                ? isEn
                  ? "Attempted Frame Assembly (AI Boards)"
                  : "Ausprobierte Rahmen-Bauweise (mit KI-Brettern)"
                : isEn
                  ? "Active Joint Topology"
                  : "Aktive Kantenstoß-Topologie"}
            </span>
          </span>
          {mode === "ai_bug" ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStackOrder("between")}
                className={`min-h-[44px] flex-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                  stackOrder === "between"
                    ? "border-purple-500 bg-purple-600 text-white dark:bg-purple-500"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-purple-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                }`}
              >
                Deckel zwischen Seiten (Bild 2)
              </button>
              <button
                type="button"
                onClick={() => setStackOrder("on_top")}
                className={`min-h-[44px] flex-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                  stackOrder === "on_top"
                    ? "border-purple-500 bg-purple-600 text-white dark:bg-purple-500"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-purple-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                }`}
              >
                Deckel auf Seiten (KI-Ausrede)
              </button>
            </div>
          ) : (
            <div className="flex min-h-[44px] items-center rounded-lg border border-neutral-200 bg-white/80 px-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
              {mode === "user_hack" &&
                "25,2 × 18,2 cm Platten links/rechts als Wangen; Kern 13,5 cm breit dazwischen."}
              {mode === "fix_between" &&
                "Seitenwände laufen über volle Außenhöhe (25,2 cm); Deckel/Boden (15,0 cm) dazwischen."}
              {mode === "fix_on_top" &&
                "Deckel/Boden laufen über volle Außenbreite (18,2 cm); Seitenwände (22,0 cm) dazwischen."}
            </div>
          )}
        </div>
      </div>

      {/* Cut list & explicit math verification table */}
      <div className="overflow-x-auto rounded-xl border border-neutral-200/80 dark:border-neutral-800">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-100/70 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900/80 dark:text-neutral-300">
              <th className="p-2.5 font-semibold">Bauteil (2× je Box)</th>
              <th className="p-2.5 font-semibold">Zuschnittmaß</th>
              <th className="p-2.5 font-semibold">Expliziter Rechenweg (Soll: 15,0 × 22,0 × 13,5 cm innen)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200/70 dark:divide-neutral-800/70">
            <tr>
              <td className="p-2.5 font-medium">Plattenpaar A (Groß)</td>
              <td className="p-2.5 font-mono font-semibold text-purple-700 dark:text-purple-300">
                {calc.frontDim}
              </td>
              <td className="p-2.5 font-mono text-neutral-600 dark:text-neutral-400">
                {calc.frontFormula}
              </td>
            </tr>
            <tr>
              <td className="p-2.5 font-medium">Plattenpaar B (Mittel)</td>
              <td className="p-2.5 font-mono font-semibold text-purple-700 dark:text-purple-300">
                {calc.sideDim}
              </td>
              <td className="p-2.5 font-mono text-neutral-600 dark:text-neutral-400">
                {calc.sideFormula}
              </td>
            </tr>
            <tr>
              <td className="p-2.5 font-medium">Plattenpaar C (Klein)</td>
              <td className="p-2.5 font-mono font-semibold text-purple-700 dark:text-purple-300">
                {calc.topDim}
              </td>
              <td className="p-2.5 font-mono text-neutral-600 dark:text-neutral-400">
                {calc.topFormula}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Invariant Gate & Volume Results (Fixed height structure to prevent CLS) */}
      <div
        className={`flex min-h-[148px] flex-col justify-between gap-3 rounded-xl border p-4 transition-colors ${
          calc.isFlush
            ? "border-emerald-500/30 bg-emerald-500/5 dark:border-emerald-500/30 dark:bg-emerald-500/10"
            : mode === "user_hack"
              ? "border-amber-500/30 bg-amber-500/5 dark:border-amber-500/30 dark:bg-amber-500/10"
              : "border-rose-500/30 bg-rose-500/5 dark:border-rose-500/30 dark:bg-rose-500/10"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {calc.isFlush ? (
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            ) : mode === "user_hack" ? (
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            ) : (
              <XCircle className="h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400" aria-hidden="true" />
            )}
            <span className="font-semibold text-neutral-900 dark:text-white">
              {calc.isFlush
                ? "Invarianten-Check bestanden: Alle Stoßkanten schließen bündig ab"
                : mode === "user_hack"
                  ? "Geometrischer Rettungs-Hack: Korpus bündig + 3,2 cm Standkufen unten"
                  : "Invarianten-Check fehlgeschlagen: Kantenstoß geht geometrisch nicht auf"}
            </span>
          </div>
          <span className="rounded-md bg-neutral-900/10 px-2.5 py-1 font-mono text-xs font-semibold text-neutral-800 dark:bg-white/10 dark:text-neutral-200">
            Innenvolumen: {calc.volL.toFixed(2)} l (Ziel: {targetVolL.toFixed(2)} l)
          </span>
        </div>

        <div className="grid grid-cols-1 gap-2 font-mono text-xs sm:grid-cols-2">
          <div className="rounded-lg bg-white/70 p-2.5 dark:bg-neutral-900/70">
            <span className="block text-[10px] font-sans font-semibold tracking-wider text-neutral-500 uppercase">
              Prüfung Achse 1 (Höhe / Überstand)
            </span>
            <span className="mt-0.5 block text-neutral-800 dark:text-neutral-200">
              {calc.invariantH}
            </span>
          </div>
          <div className="rounded-lg bg-white/70 p-2.5 dark:bg-neutral-900/70">
            <span className="block text-[10px] font-sans font-semibold tracking-wider text-neutral-500 uppercase">
              Prüfung Achse 2 (Breite / Tiefe)
            </span>
            <span className="mt-0.5 block text-neutral-800 dark:text-neutral-200">
              {calc.invariantW}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-600 dark:text-neutral-400">
          <span>
            Reales Innenmaß der Kammer: <strong className="font-mono">{calc.innerDims}</strong>
          </span>
          <span>
            Abweichung zum Zielvolumen:{" "}
            <strong className="font-mono">
              {((calc.volL - targetVolL) >= 0 ? "+" : "") +
                (calc.volL - targetVolL).toFixed(2)}{" "}
              Liter ({(((calc.volL - targetVolL) / targetVolL) * 100).toFixed(1)} %)
            </strong>
          </span>
        </div>
      </div>
    </div>
  );
}
